// Agent modes (plan 101 slice 06, L3) — per org / repo, switch a box mode on or off
// and NARROW the `code.*` tools it offers the model. Pure: the ceilings table, the
// write shape, the resolver, and the wire audit. No I/O — the `agent_modes` rows are
// read in `@developerz/db` and handed in.

import { z } from 'zod';
import { PromptRole } from './system-prompt';

/**
 * THE STORED MODE VOCABULARY — every key `agent_modes.mode_key` may hold
 * (`agent_modes_mode_key_ck`, pinned to this list by `hold-reason-constraint.test.ts`).
 * Nine keys, in two groups that are NOT one list (#7008, epic #6552 "roles on machines"):
 *
 *   - `scout`, `worker`, `reviewer`, `devops` and `triage` are stored {@link PromptRole}
 *     values, the SAME key `system_prompts.role` files a role's prose under. Coder's key
 *     stays `worker` (no rename to `coder`).
 *   - `ci_fixer`, `planner`, `gardener` and `escalation` are PLATFORM keys: a role switch
 *     with no customer-authorable prose slot behind it.
 *
 * A key being IN this vocabulary is not a door that writes it. Which (scope, key) pairs a
 * write may store is {@link AGENT_MODE_WRITABLE}, enforced at ONE seam (`writableRow`,
 * `@developerz/db` `queries/agent-modes.ts`). `triage`, `gardener` and `escalation` are
 * machine-only forever (`agent_modes_machine_only_key_ck`); `setup`, `janitor` and
 * `architect` are not keys at all.
 *
 * Which key holds a claimed task is `AGENT_MODE_KEY_FOR_ORIGIN`
 * (`value-objects/agent-roster.ts`, #7390), one exhaustive table over `TaskOrigin` that the
 * claim gate and the assigner both read. It is not the prose selector: `promptRoleForEnvelope`
 * still picks the prose a run reads, so a `pr_babysit` round reads worker prose under the
 * `ci_fixer` switch.
 */
export const AGENT_MODE_KEYS = [
  PromptRole.Scout,
  PromptRole.Worker,
  PromptRole.Reviewer,
  PromptRole.Devops,
  'ci_fixer',
  'planner',
  PromptRole.Triage,
  'gardener',
  'escalation',
] as const;
export const AgentModeKeySchema = z.enum(AGENT_MODE_KEYS);
export type AgentModeKey = z.infer<typeof AgentModeKeySchema>;

/**
 * Whether a string is a key of the stored mode vocabulary. NOT whether any door may write
 * it at some scope ({@link agentModeWritable} answers that), and NOT whether a task runs
 * under it (`AGENT_MODE_KEY_FOR_ORIGIN`).
 */
export function isAgentModeKey(value: string): value is AgentModeKey {
  return (AGENT_MODE_KEYS as readonly string[]).includes(value);
}

/**
 * Every `code.*` tool a box can mount: every `CODE_TOOLS` member plus the port-gated
 * `code.recall`, `code.todo` and `code.explore`. A literal list because this package imports nothing but zod — the
 * runner's guard pins it against the `@developerz/tools` catalog, so a tool added
 * there fails a test instead of silently being un-narrowable.
 *
 * ADDING A NAME HERE IS TWO EDITS, and the second is not optional: every tool also
 * declares its permission family in {@link AGENT_MODE_CODE_TOOL_FAMILY}, which is
 * `Record<AgentModeCodeTool, …>` and therefore TOTAL — a name added here with no family
 * is a type error at that declaration. The family, never the SPELLING, is what a
 * maintainer's `permission: {bash: deny}` subtracts (#4587): a tool that runs a command
 * is `shell` whether it is called `code.bash` or `code.exec`.
 */
export const AGENT_MODE_CODE_TOOLS = [
  'code.read',
  'code.read_many',
  'code.grep',
  'code.glob',
  'code.changes',
  'code.edit',
  'code.write',
  'code.bash',
  'code.multi_bash',
  'code.bash_background',
  'code.bash_output',
  'code.bash_kill',
  'code.bash_list',
  'code.recall',
  'code.web_fetch',
  'code.web_search',
  'code.todo',
  'code.explore',
] as const;
export const AgentModeCodeToolSchema = z.enum(AGENT_MODE_CODE_TOOLS);
export type AgentModeCodeTool = z.infer<typeof AgentModeCodeToolSchema>;

/**
 * THE PERMISSION FAMILIES — what a repo agent definition's `permission: {<name>: deny}`
 * takes away (`@developerz/policy` `repo-specialist.ts`, which maps each declared name
 * onto a family). A family is a CAPABILITY, not a prefix: it says what the tool DOES.
 *
 *   - `shell` — runs an arbitrary command. Fenced by `bash: deny`.
 *   - `read`  — returns a named file's contents. Fenced by `read: deny`.
 *   - `edit`  — creates or changes a file. Fenced by `edit: deny` (and, narrowly, by
 *     Claude Code's own `write` / `multiedit` names).
 *   - `grep`  — locates by CONTENT, returning matches. Fenced by `grep: deny`.
 *   - `glob`  — locates by PATH pattern. Fenced by `glob: deny`.
 *   - `webfetch` — reads a page off the public web. Fenced by `webfetch: deny`.
 *   - `websearch` — queries a public web search. Fenced by `websearch: deny`.
 *   - `none`  — no name in either ecosystem's permission vocabulary fences it, so
 *     nothing denies it. An explicit decision, held by `agent-mode.test.ts` to the tools
 *     that touch neither the tree nor a command: `code.recall` (the repo wiki) and
 *     `code.todo` (the run's own checklist); a tool that runs a command is `shell`
 *     whatever it is called.
 *
 * `grep` and `glob` are two families and not one `search` family because Claude Code and
 * opencode declare them as two permissions: `grep: deny` must not take `code.glob` away.
 * `webfetch` and `websearch` are two for the same reason: opencode's permissions are
 * `webfetch` and `websearch`, Claude Code's tools `WebFetch` and `WebSearch`, and both
 * ecosystems' names lowercase to the family name.
 */
export const AGENT_MODE_TOOL_FAMILIES = [
  'shell',
  'read',
  'edit',
  'grep',
  'glob',
  'webfetch',
  'websearch',
  'none',
] as const;
export type AgentModeToolFamily = (typeof AGENT_MODE_TOOL_FAMILIES)[number];

/**
 * EVERY coding tool's family — `Record<AgentModeCodeTool, …>`, so it is TOTAL by its
 * type and a name added to {@link AGENT_MODE_CODE_TOOLS} that picks no family fails
 * to compile HERE rather than shipping as a tool no `deny` can reach.
 *
 * By family: `shell` is `code.bash`, `code.multi_bash`, `code.bash_background`,
 * `code.bash_output`, `code.bash_kill`, `code.bash_list`; `read` is `code.read`,
 * `code.read_many` and `code.changes` (a diff IS the named files' contents, so
 * `read: deny` must take it away with them); `edit` is `code.edit`, `code.write`;
 * `grep` is `code.grep`; `glob` is `code.glob`; `webfetch` is `code.web_fetch`;
 * `websearch` is `code.web_search`; `none` is `code.recall` and `code.todo`.
 * `code.explore` is `read`: its sub-agent returns what the files say, so `read: deny`
 * must take it away with `code.read`.
 *
 * Key order is the catalog's, and its test pins that — {@link agentModeToolsInFamily}
 * returns a family in this order, and a seat's tool list is read by people.
 */
export const AGENT_MODE_CODE_TOOL_FAMILY: Readonly<Record<AgentModeCodeTool, AgentModeToolFamily>> =
  {
    'code.read': 'read',
    'code.read_many': 'read',
    'code.grep': 'grep',
    'code.glob': 'glob',
    'code.changes': 'read',
    'code.edit': 'edit',
    'code.write': 'edit',
    'code.bash': 'shell',
    'code.multi_bash': 'shell',
    'code.bash_background': 'shell',
    'code.bash_output': 'shell',
    'code.bash_kill': 'shell',
    'code.bash_list': 'shell',
    'code.recall': 'none',
    'code.web_fetch': 'webfetch',
    'code.web_search': 'websearch',
    'code.todo': 'none',
    'code.explore': 'read',
  };

/**
 * One family's tools, in the record's key order.
 *
 * Generic over the family record rather than closed over
 * {@link AGENT_MODE_CODE_TOOL_FAMILY} so a guard can prove the grouping reads the TAG
 * and not the name, by handing it a catalog this platform does not ship yet — a
 * `code.exec` tagged `shell`. That is the whole defect of #4587: with the family read
 * off the spelling (`name.includes('bash')`), such a tool is granted to a seat whose
 * maintainer denied the shell, and the audit line still reports the fence as applied.
 */
export function agentModeToolsInFamily<Name extends string>(
  families: Readonly<Record<Name, AgentModeToolFamily>>,
  family: AgentModeToolFamily,
): ReadonlyArray<Name> {
  return (Object.keys(families) as Name[]).filter((name) => families[name] === family);
}

/**
 * The scout survey's slice — the runner's `READ_ONLY_CODING_TOOLS` plus the two web
 * reads (`WEB_CODING_TOOLS`): a survey grounding a plan in a library's current API reads
 * its docs, and neither web tool changes the checkout or runs a command on it.
 */
const SURVEY_READ_TOOLS = [
  'code.read',
  'code.read_many',
  'code.grep',
  'code.glob',
  'code.web_fetch',
  'code.web_search',
] as const satisfies readonly AgentModeCodeTool[];

/**
 * THE CEILING per mode — what `code_tools: null` means, and what any allowlist must
 * be a subset of. A mode NARROWS; it never widens past what its lane offers today.
 * `reviewer` is empty: the review brain offers the model no tools (its codegraph
 * calls are the harness's own), so a reviewer mode is on/off only. `ci_fixer` takes the
 * worker's: the `pr_babysit` executor IS the coding adapter wrapped with a fix brief
 * (`apps/runner/src/modes/router.ts` `AutonomousExecutors.babysit`). `planner`, `triage`,
 * `gardener` and `escalation` are empty, on/off only, like `reviewer`.
 */
export const AGENT_MODE_TOOL_CEILINGS: Readonly<
  Record<AgentModeKey, readonly AgentModeCodeTool[]>
> = {
  scout: SURVEY_READ_TOOLS,
  worker: AGENT_MODE_CODE_TOOLS,
  devops: AGENT_MODE_CODE_TOOLS,
  reviewer: [],
  ci_fixer: AGENT_MODE_CODE_TOOLS,
  planner: [],
  triage: [],
  gardener: [],
  escalation: [],
};

/** The tools a coding lane cannot run without — narrowing below this only guarantees failed runs. */
const EDIT_FLOOR = [
  'code.read',
  'code.edit',
  'code.write',
] as const satisfies readonly AgentModeCodeTool[];

/**
 * The survey's floor: `code.read` to open a file and `code.grep` to find one. With
 * neither, every scout survey runs blind and grounds its plan in nothing. `code.grep`
 * over `code.glob` because a survey locates by CONTENT (a symbol, an error string),
 * and grep answers the path question too; glob stays narrowable.
 */
const SURVEY_FLOOR = ['code.read', 'code.grep'] as const satisfies readonly AgentModeCodeTool[];

/**
 * The FLOOR per mode — an allowlist that omits one of these is refused at write AND at
 * resolve. Ceilings and floors are STORED-ROW SHAPE: a stored row is re-validated
 * against them at resolve, where a violation throws, so shrinking a ceiling or growing
 * a floor can break rows that were valid when written. `agent-mode.test.ts` pins both
 * tables exactly — change them with a plan for the stored rows, never alone.
 */
export const AGENT_MODE_TOOL_FLOORS: Readonly<Record<AgentModeKey, readonly AgentModeCodeTool[]>> =
  {
    scout: SURVEY_FLOOR,
    worker: EDIT_FLOOR,
    devops: EDIT_FLOOR,
    reviewer: [],
    ci_fixer: EDIT_FLOOR,
    planner: [],
    triage: [],
    gardener: [],
    escalation: [],
  };

/**
 * The violations of a (known-tool) allowlist for one mode: outside the ceiling,
 * below the floor, duplicated. One function, read by the write schema and the
 * resolver, so the two seams cannot enforce different rules.
 *
 * An EMPTY-CEILING key (`reviewer`, `planner`, `triage`, `gardener`, `escalation`) refuses
 * a NON-EMPTY list with one sentence — the reviewer rule, generalised to every such key
 * (#7008). An empty list `[]` stays valid, exactly as it always was for `reviewer`: a stored
 * reviewer row with `code_tools: []` (or whose tools were all deleted since, so `kept = []`
 * at resolve) must keep resolving, or that account's review-lane claim rolls back.
 */
function allowlistViolations(key: AgentModeKey, tools: readonly string[]): string[] {
  const ceiling: readonly string[] = AGENT_MODE_TOOL_CEILINGS[key];
  const out: string[] = [];
  if (ceiling.length === 0 && tools.length > 0) {
    out.push(
      `${key} offers the model no tools, so code_tools must be null — got [${tools.join(', ')}]; a ${key} mode is enable/disable only`,
    );
    return out;
  }
  const outside = tools.filter((t) => !ceiling.includes(t));
  if (outside.length > 0) {
    out.push(
      `${key} code_tools may only narrow its ceiling [${ceiling.join(', ')}]; outside it: [${outside.join(', ')}]`,
    );
  }
  const missing = AGENT_MODE_TOOL_FLOORS[key].filter((t) => !tools.includes(t));
  if (missing.length > 0) {
    out.push(
      `${key} code_tools must keep the floor [${AGENT_MODE_TOOL_FLOORS[key].join(', ')}]; missing: [${missing.join(', ')}]`,
    );
  }
  if (new Set(tools).size !== tools.length) out.push(`${key} code_tools lists a tool twice`);
  return out;
}

/**
 * The WRITE shape (API, MCP, dashboard). `code_tools: null` = the mode's full
 * ceiling; a list is an ALLOWLIST of known tool names, validated per mode by
 * {@link allowlistViolations}.
 */
export const AgentModeRowSchema = z
  .object({
    mode_key: AgentModeKeySchema,
    enabled: z.boolean(),
    code_tools: z.array(AgentModeCodeToolSchema).nullable(),
  })
  .strict()
  .superRefine((row, ctx) => {
    if (row.code_tools === null) return;
    for (const message of allowlistViolations(row.mode_key, row.code_tools)) {
      ctx.addIssue({ code: 'custom', path: ['code_tools'], message });
    }
  });
export type AgentModeRow = z.infer<typeof AgentModeRowSchema>;

/**
 * The scope of a stored `agent_modes` row: `org` (`scope_ref` `''`), `repo` (`scope_ref` =
 * the repository id) or `machine` (`scope_ref` = `runners.id` as text, never `''`), the
 * pairing `agent_modes_scope_ck` enforces. A machine row carries `enabled` only
 * (`agent_modes_machine_shape_ck`: `code_tools` is null). It is ANDed with the org/repo
 * answer and never re-enables an org's or a repo's "off". The readers that decide work, the
 * claim's separate conjunct and the placement proxy (`machineRoleOffSql`, `@developerz/db`)
 * and the assigner's demand read (`listMachineRoleOffKeys`), each match `enabled = false`
 * only, so a machine row with `enabled = true` is inert. It never joins the org/repo
 * resolution (`resolveAgentModeForTask`, `winningModeRowSql`).
 */
export const AgentModeScopeSchema = z.enum(['org', 'repo', 'machine']);
export type AgentModeScope = z.infer<typeof AgentModeScopeSchema>;

/** Where a resolution came from — a stored row's scope, or the code default. */
export const AgentModeSourceSchema = z.enum(['repo', 'org', 'machine', 'default']);
export type AgentModeSource = z.infer<typeof AgentModeSourceSchema>;

/**
 * THE WRITE MATRIX — which (scope, key) pairs any door may store, as ONE typed table of
 * literal tuples. A pair outside it is refused at the one write seam (`writableRow`,
 * `@developerz/db` `queries/agent-modes.ts`) with {@link AgentModeWriteRefusedError}, so the
 * REST PUT, the MCP `org_set` / `repo_set` and the worker's revert pause all hold it. The
 * clear doors and every list iterate their scope's tuple, so a list answers exactly the
 * keys its scope can write. A record typed over a scope's tuple
 * ({@link AgentModeWritableKey}) is a type error at every such record the day the tuple
 * widens.
 *
 * Permanent. #7008 opened (org | repo) x (`scout`, `worker`, `reviewer`, `devops`) with an
 * empty machine tuple; #7390 opened org and repo `ci_fixer`; #6756 opened machine `worker`,
 * `ci_fixer`, `reviewer`, `scout`, `devops`; #6758 opened machine `escalation` and #6762
 * machine `triage`, each appended LAST (a pin taking the tuple's first element as a work role
 * stays one); #6763 opened machine `gardener` just BEFORE `triage`, because #6762's fence pins
 * `triage` as the tuple's last element (`agent-mode.machine.test.ts`). A later slice opens the rest: #6764 org, repo and
 * machine `planner`. `triage`, `gardener` and `escalation` never open at org or repo
 * (`agent_modes_machine_only_key_ck`). The machine last-on rule counts only
 * {@link AGENT_MODE_MACHINE_WORK_KEYS}: every machine work key off is `last_role_on`.
 */
export const AGENT_MODE_WRITABLE = {
  org: ['scout', 'worker', 'reviewer', 'devops', 'ci_fixer'],
  repo: ['scout', 'worker', 'reviewer', 'devops', 'ci_fixer'],
  machine: [
    'worker',
    'ci_fixer',
    'reviewer',
    'scout',
    'devops',
    'escalation',
    'gardener',
    'triage',
  ],
} as const satisfies { readonly [S in AgentModeScope]: readonly AgentModeKey[] };

/**
 * The machine keys that make a machine CLAIM work — {@link AGENT_MODE_WRITABLE}`.machine`
 * minus `escalation` (#6758). Escalation only decorates a coding claim's credential and runs
 * no work of its own, so a machine with every work key off and escalation on claims nothing:
 * the last-on rule ({@link machineSwitchVerdict}) iterates this, never the machine tuple.
 */
export const AGENT_MODE_MACHINE_WORK_KEYS: readonly Exclude<
  AgentModeWritableKey<'machine'>,
  'escalation'
>[] = AGENT_MODE_WRITABLE.machine.filter(
  (k): k is Exclude<AgentModeWritableKey<'machine'>, 'escalation'> => k !== 'escalation',
);

/** The keys one scope's doors may write — the type a per-scope record is keyed on. */
export type AgentModeWritableKey<S extends AgentModeScope> =
  (typeof AGENT_MODE_WRITABLE)[S][number];

/** Whether a door may store `key` at `scope` — the {@link AGENT_MODE_WRITABLE} lookup. */
export function agentModeWritable<S extends AgentModeScope>(
  scope: S,
  key: string,
): key is AgentModeWritableKey<S> {
  // Widened to `readonly string[]` because `key` is any string (an unparsed input, or a key
  // of another scope): `includes` on the literal tuple type would refuse the very argument
  // this lookup exists to test. The cast only widens; it asserts nothing about the tuple.
  return (AGENT_MODE_WRITABLE[scope] as readonly string[]).includes(key);
}

/**
 * A write refused because the write matrix closes its (scope, key) pair. Thrown by the one
 * write seam before any SQL; the api control answers it as `invalid_mode` (a 422, never a
 * 500), and the MCP verb returns its sentence as the tool's error text.
 */
export class AgentModeWriteRefusedError extends Error {
  readonly scope: AgentModeScope;
  readonly modeKey: AgentModeKey;
  constructor(scope: AgentModeScope, modeKey: AgentModeKey) {
    const open: readonly string[] = AGENT_MODE_WRITABLE[scope];
    super(
      `the ${scope} scope cannot set the ${modeKey} mode — the pair (${scope}, ${modeKey}) is closed by the write matrix; ` +
        `the ${scope} scope sets ${open.length === 0 ? 'no mode yet' : open.join(', ')}`,
    );
    this.name = 'AgentModeWriteRefusedError';
    this.scope = scope;
    this.modeKey = modeKey;
  }
}

/**
 * A machine write or clear addressed a runner that is not one of the writer's own org
 * machines: no such id, another account's box, or a user-owned (private) box shown read-only
 * from this org (owner ruling G23). Thrown by the one write seam's machine arm (`writableRow`,
 * `@developerz/db`) after its locked `runners` read finds no row; the api control answers it
 * as `machine_not_found` (a 404, the same as an unknown id, never a 403 that confirms
 * another tenant's machine).
 */
export class AgentModeMachineNotFoundError extends Error {
  readonly runnerId: string;
  constructor(runnerId: string) {
    super(
      `machine ${JSON.stringify(runnerId)} is not one of this account's own org machines, so no machine agent mode can be addressed under that id`,
    );
    this.name = 'AgentModeMachineNotFoundError';
    this.runnerId = runnerId;
  }
}

/** What {@link machineSwitchVerdict} reads of one stored machine row. */
export type AgentModeMachineStoredRow = {
  readonly mode_key: AgentModeKey;
  readonly enabled: boolean;
};

/**
 * The verdict on one machine switch PUT, over that machine's stored rows (#6756). Pure.
 *
 *   - `noop`: `enabled` already IS the key's effective value on this machine. No row reads as
 *     on, so `enabled: true` on a key with no row stores nothing and audits nothing.
 *   - `last_role_on`: `enabled: false` would leave every machine work key
 *     ({@link AGENT_MODE_MACHINE_WORK_KEYS}) effectively off. The server keeps at least one
 *     on. A key outside that set (`escalation`, #6758) is never refused this way, and its
 *     being on never keeps a machine's last work role on.
 *   - `write`: anything else.
 *
 * NOT the AND rule: whether a role actually runs on the machine is the org/repo answer ANDed
 * with the machine row, decided in SQL only (`machineRoleOffSql`, read by the claim and the
 * placement proxy). This decides only what one machine's own switches may store.
 */
export function machineSwitchVerdict(input: {
  readonly rows: readonly AgentModeMachineStoredRow[];
  readonly modeKey: AgentModeWritableKey<'machine'>;
  readonly enabled: boolean;
}): 'noop' | 'last_role_on' | 'write' {
  const on = (key: AgentModeKey): boolean =>
    input.rows.find((r) => r.mode_key === key)?.enabled ?? true;
  if (on(input.modeKey) === input.enabled) return 'noop';
  if (input.enabled) return 'write';
  // Widened like `agentModeWritable`'s lookup: `modeKey` may be a non-work machine key.
  if (!(AGENT_MODE_MACHINE_WORK_KEYS as readonly string[]).includes(input.modeKey)) return 'write';
  const othersOn = AGENT_MODE_MACHINE_WORK_KEYS.some((k) => k !== input.modeKey && on(k));
  return othersOn ? 'write' : 'last_role_on';
}

/**
 * What the resolver needs of a stored row. `code_tools` is `string[]`, not the tool
 * enum, on purpose: a name stored before its tool was deleted from code must reach
 * the resolver to be reported in `dropped_tools`, not fail the read.
 */
export type AgentModeStoredRow = {
  readonly mode_key: AgentModeKey;
  readonly enabled: boolean;
  readonly code_tools: readonly string[] | null;
  readonly revision: number;
};

export type AgentModeResolution = {
  readonly key: AgentModeKey;
  readonly enabled: boolean;
  /** The winning row's revision; 0 for the code default. */
  readonly revision: number;
  readonly source: AgentModeSource;
  /** null = the full ceiling (no narrowing); otherwise the allowlist, in ceiling order. */
  readonly code_tools: readonly AgentModeCodeTool[] | null;
  /** Stored names no longer in code — dropped from the allowlist and shipped on the audit. */
  readonly dropped_tools: readonly string[];
};

/** Thrown when a stored row violates what the write seam would have refused. */
export class AgentModeRowInvalidError extends Error {
  constructor(key: AgentModeKey, source: AgentModeSource, problems: readonly string[]) {
    super(
      `[agent-mode] the ${source} agent_modes row for "${key}" is invalid: ${problems.join('; ')}. ` +
        'It bypassed the write seam (AgentModeRowSchema); fix or delete the row.',
    );
    this.name = 'AgentModeRowInvalidError';
  }
}

/**
 * Resolve one mode: repo row > org row > code default (enabled, full ceiling,
 * revision 0). Pure.
 *
 * A stored name that is not a code tool at all (deleted since the write) is
 * intersected away and reported in `dropped_tools` — visible, never silent. A stored
 * KNOWN tool outside the ceiling, a list below the floor, or a row handed in under
 * another key cannot come from the write seam, so it THROWS rather than degrade to
 * the full tool set.
 */
export function resolveAgentMode(input: {
  readonly key: AgentModeKey;
  readonly orgRow: AgentModeStoredRow | null;
  readonly repoRow: AgentModeStoredRow | null;
}): AgentModeResolution {
  const { key } = input;
  const winner: { row: AgentModeStoredRow; source: AgentModeSource } | null = input.repoRow
    ? { row: input.repoRow, source: 'repo' }
    : input.orgRow
      ? { row: input.orgRow, source: 'org' }
      : null;
  if (winner === null) {
    return {
      key,
      enabled: true,
      revision: 0,
      source: 'default',
      code_tools: null,
      dropped_tools: [],
    };
  }
  const { row, source } = winner;
  if (row.mode_key !== key) {
    throw new AgentModeRowInvalidError(key, source, [`row carries mode_key "${row.mode_key}"`]);
  }
  const known: readonly string[] = AGENT_MODE_CODE_TOOLS;
  const stored = row.code_tools;
  const kept = stored === null ? null : stored.filter((t) => known.includes(t));
  const dropped = stored === null ? [] : stored.filter((t) => !known.includes(t));
  if (kept !== null) {
    const problems = allowlistViolations(key, kept);
    if (problems.length > 0) throw new AgentModeRowInvalidError(key, source, problems);
  }
  const ceiling = AGENT_MODE_TOOL_CEILINGS[key];
  return {
    key,
    enabled: row.enabled,
    revision: row.revision,
    source,
    code_tools: kept === null ? null : ceiling.filter((t) => kept.includes(t)),
    dropped_tools: dropped,
  };
}

/**
 * A tool name ON THE WIRE: any `code.*` name, not the domain enum — a box built
 * against an older domain must not reject a whole assignment over a tool name it
 * has not heard of (it simply cannot mount it).
 */
const WireCodeToolSchema = z.string().regex(/^code\.[a-z_]+$/);

/**
 * The `agent_mode` audit + narrowing on a fleet assignment (and on the task's audit).
 * No `enabled`: a disabled mode is HELD at the claim gate, never assigned, so no
 * producer here writes one. Named `agent_mode`, never `mode` (that means live/replay
 * on audit rows).
 *
 * FORWARD-COMPATIBLE ON PURPOSE, unlike the write shape and the echo: NOT `.strict()`,
 * and `key` / `source` are strings, not the closed enums. It nests inside the
 * deliberately non-strict `FleetTaskAssignmentSchema`; a strict object or a closed
 * enum here would make a box on this build reject EVERY assignment the day a later
 * mothership adds a field, a mode key or a scope. The box decides what an unknown
 * key means (`isAgentModeKey`); the parse must not decide it by refusing the task.
 */
export const AgentModeAssignmentSchema = z.object({
  key: z.string().min(1),
  revision: z.number().int().nonnegative(),
  source: z.string().min(1),
  code_tools: z.array(WireCodeToolSchema).nullable(),
  dropped_tools: z.array(z.string().min(1)),
});
export type AgentModeAssignment = z.infer<typeof AgentModeAssignmentSchema>;

/**
 * The box's ECHO on `prompt_composed`: the mode it actually applied and the `code.*`
 * tools it actually MOUNTED after narrowing. Its presence is the proof narrowing ran —
 * a box built before this field sends none, which is how the audit tells an old box
 * that ignored `agent_mode` from a new one that applied it.
 */
export const AgentModeEchoSchema = z
  .object({
    key: AgentModeKeySchema,
    revision: z.number().int().nonnegative(),
    applied_tools: z.array(WireCodeToolSchema),
  })
  .strict();
export type AgentModeEcho = z.infer<typeof AgentModeEchoSchema>;

/** The wire form of an ENABLED resolution. Throws on a disabled one — that task should have been held. */
export function agentModeAssignmentOf(resolution: AgentModeResolution): AgentModeAssignment {
  if (!resolution.enabled) {
    throw new Error(
      `[agent-mode] mode "${resolution.key}" is disabled (${resolution.source} rev ${String(resolution.revision)}); ` +
        'a disabled mode is held by the claim gate (mode_disabled) and must never reach an assignment',
    );
  }
  return {
    key: resolution.key,
    revision: resolution.revision,
    source: resolution.source,
    code_tools: resolution.code_tools === null ? null : [...resolution.code_tools],
    dropped_tools: [...resolution.dropped_tools],
  };
}
